import { useEffect, useState } from "react";
import type { ExpertSummary } from "../api";
import { api } from "../api";
import { ExpertMark } from "../components/design/ExpertMark";
import { Segmented } from "../components/design/Segmented";
import { Icon } from "../components/design/Icon";
import { EmptyState } from "../components/design/EmptyState";
import { navigate, routes } from "../router";

// Frame data shape per the design handoff. Mirrors what
// add-screen-share-frame-capture is expected to expose via
// GET /v1/meetings/{id}/frames; until that lands, this page renders an
// empty state on production meetings.
export type FrameKind = "diagram" | "whiteboard" | "ui" | "code";

export interface Frame {
  id: string;
  t: string;
  kind: FrameKind;
  label: string;
  summary: string;
  capturedBy: "auto" | "manual";
  relevantTo: string[];
  notes: { expert_id: string; text: string }[];
  referencedIn: string[];
  /** 1-hour signed URL for the frame's JPG. Empty when signing
   *  failed at the api — caller falls back to the striped placeholder. */
  signed_url: string;
}

type Filter = FrameKind | "all";
const KIND_OPTIONS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: "all", label: "All" },
  { value: "diagram", label: "Diagram" },
  { value: "whiteboard", label: "Whiteboard" },
  { value: "ui", label: "UI" },
  { value: "code", label: "Code" },
];

const KIND_TINT: Record<FrameKind, { tint: string; deep: string }> = {
  diagram: { tint: "var(--spec-tint)", deep: "var(--spec-deep)" },
  whiteboard: { tint: "var(--str-tint)", deep: "var(--str-deep)" },
  ui: { tint: "var(--gdpr-tint)", deep: "var(--gdpr-deep)" },
  code: { tint: "var(--paper-3)", deep: "var(--ink-2)" },
};

export function Frames({ meetingId }: { meetingId: string }) {
  const [frames, setFrames] = useState<Frame[] | null>(null);
  const [experts, setExperts] = useState<ExpertSummary[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.listExperts().then(setExperts).catch(() => {});
    // `/v1/meetings/{id}/frames` now exists and returns an empty list
    // until the frame-capture worker pipeline lands (Recall video WS +
    // H.264 decode + Vertex Flash classifier). Empty list → empty state.
    api
      .listMeetingFrames(meetingId)
      .then((views) => {
        if (!alive) return;
        // Map the api's loose `kind: string` onto the SPA's narrowed
        // `FrameKind` union; unknown values fall through to "code"
        // (the most neutral placeholder).
        const projected: Frame[] = views.map((v) => ({
          id: v.id,
          t: v.t,
          kind: (["diagram", "whiteboard", "ui", "code"].includes(v.kind)
            ? v.kind
            : "code") as FrameKind,
          label: v.label,
          summary: v.summary,
          capturedBy: v.capturedBy,
          relevantTo: v.relevantTo,
          notes: v.notes,
          referencedIn: v.referencedIn,
          signed_url: v.signed_url,
        }));
        setFrames(projected);
      })
      .catch((e) => {
        if (alive) {
          setFrames([]);
          setError(String(e instanceof Error ? e.message : e));
        }
      });
    return () => {
      alive = false;
    };
  }, [meetingId]);

  const expertById = new Map(experts.map((e) => [e.id, e]));
  const visible = (frames ?? []).filter((f) =>
    filter === "all" ? true : f.kind === filter,
  );
  const active = activeId ? frames?.find((f) => f.id === activeId) : null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <header
        style={{
          padding: "14px 24px",
          borderBottom: "1px solid var(--rule)",
          background: "var(--card)",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <button
          type="button"
          className="btn sm"
          onClick={() => navigate(routes.meeting(meetingId))}
        >
          <Icon name="arrowL" size={12} /> Back
        </button>
        <span className="mono dim" style={{ fontSize: 11 }}>
          /meetings/{meetingId.slice(0, 12)}/frames
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button type="button" className="btn sm" disabled>
            <Icon name="download" size={13} /> frames.zip
          </button>
          <button type="button" className="btn primary sm" disabled>
            <Icon name="download" size={13} /> contact-sheet.pdf
          </button>
        </div>
      </header>

      <div
        className="recspec-frames-body"
        style={{
          display: "grid",
          gridTemplateColumns: active ? "1fr 380px" : "1fr",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            overflow: "auto",
            padding: "24px 32px",
            background: "var(--paper)",
          }}
        >
          <div style={{ maxWidth: 1080, margin: "0 auto" }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              /meetings/{meetingId.slice(0, 12)}/frames
            </div>
            <h1
              className="serif"
              style={{
                margin: 0,
                fontSize: 36,
                fontWeight: 400,
                letterSpacing: "-.01em",
              }}
            >
              What was on screen.
            </h1>
            <div
              className="dim"
              style={{
                marginTop: 6,
                fontSize: 13,
                maxWidth: 640,
                lineHeight: 1.5,
              }}
            >
              Frames are captured automatically on screen change or manually
              with a hotkey, then tagged by each expert. Used to review system
              designs, mocks, or code snippets — and referenced inline from the
              bundle artifacts.
            </div>

            <div
              style={{
                marginTop: 22,
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <span className="eyebrow">Kind</span>
              <Segmented<Filter>
                value={filter}
                onChange={setFilter}
                size="sm"
                options={KIND_OPTIONS}
              />
              <span
                className="mono dim"
                style={{ fontSize: 11, marginLeft: "auto" }}
              >
                {visible.length} of {frames?.length ?? 0} frames
              </span>
            </div>

            {frames === null && (
              <div className="dim" style={{ marginTop: 32 }}>
                Loading frames…
              </div>
            )}
            {frames !== null && frames.length === 0 && (
              <EmptyFramesState error={error} />
            )}
            {frames !== null && frames.length > 0 && (
              <div
                className="recspec-frames-grid"
                style={{
                  marginTop: 22,
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 16,
                }}
              >
                {visible.map((f) => (
                  <FrameCard
                    key={f.id}
                    frame={f}
                    active={activeId === f.id}
                    expertById={expertById}
                    onOpen={() =>
                      setActiveId(activeId === f.id ? null : f.id)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {active && (
          <aside
            style={{
              borderLeft: "1px solid var(--rule)",
              background: "var(--paper-2)",
              overflow: "auto",
              padding: 20,
            }}
          >
            <FrameDetail
              frame={active}
              expertById={expertById}
              onClose={() => setActiveId(null)}
            />
          </aside>
        )}
      </div>
    </div>
  );
}

function EmptyFramesState({ error }: { error: string | null }) {
  return (
    <>
      <EmptyState
        icon="frames"
        heading="No frames captured"
        subline={
          <>
            Frame capture is part of the{" "}
            <code className="mono">add-screen-share-frame-capture</code> change
            which isn't shipped yet. Once it lands, screens shared during the
            meeting will appear here automatically.
          </>
        }
      />
      {error && (
        <div
          className="dim mono"
          style={{
            textAlign: "center",
            fontSize: 10.5,
            marginTop: -8,
            marginBottom: 16,
          }}
        >
          (api note: {error})
        </div>
      )}
    </>
  );
}

function FrameCard({
  frame,
  active,
  expertById,
  onOpen,
}: {
  frame: Frame;
  active: boolean;
  expertById: Map<string, ExpertSummary>;
  onOpen: () => void;
}) {
  const kind = KIND_TINT[frame.kind];
  const experts = frame.relevantTo
    .map((id) => expertById.get(id))
    .filter((e): e is ExpertSummary => !!e);
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        textAlign: "left",
        padding: 0,
        cursor: "pointer",
        background: "var(--card)",
        border: active ? "1px solid var(--ink)" : "1px solid var(--rule)",
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: active ? "var(--sh-2)" : "var(--sh-1)",
        transition: "border-color .15s, box-shadow .15s",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          height: 156,
          position: "relative",
          background: frame.signed_url
            ? "var(--paper-2)"
            : `repeating-linear-gradient(135deg, ${kind.tint} 0 12px, var(--card) 12px 24px)`,
          borderBottom: "1px solid var(--rule)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {frame.signed_url ? (
          <img
            src={frame.signed_url}
            alt={frame.label}
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <span
            className="mono"
            style={{
              fontSize: 11,
              padding: "5px 10px",
              borderRadius: 4,
              background: "var(--card)",
              border: "1px solid var(--rule)",
              color: kind.deep,
              fontWeight: 500,
            }}
          >
            {frame.label}
          </span>
        )}
      </div>
      <div style={{ padding: "10px 12px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 4,
          }}
        >
          <span className="mono" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
            {frame.t}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              padding: "1px 6px",
              borderRadius: 3,
              background: kind.tint,
              color: kind.deep,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              fontWeight: 600,
            }}
          >
            {frame.kind}
          </span>
          {frame.capturedBy === "auto" ? (
            <span className="mono dim" style={{ fontSize: 9.5, marginLeft: "auto" }}>
              auto
            </span>
          ) : (
            <span
              className="mono"
              style={{ fontSize: 9.5, marginLeft: "auto", color: "var(--ok)" }}
            >
              manual
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
          {frame.label}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>
          {frame.summary}
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 8, alignItems: "center" }}>
          {experts.map((e) => (
            <ExpertMark key={e.id} expert={e} size={18} />
          ))}
          {frame.notes.length > 0 && (
            <span
              className="mono dim"
              style={{ fontSize: 10, marginLeft: "auto" }}
            >
              {frame.notes.length} note{frame.notes.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function FrameDetail({
  frame,
  expertById,
  onClose,
}: {
  frame: Frame;
  expertById: Map<string, ExpertSummary>;
  onClose: () => void;
}) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div className="eyebrow">Frame · {frame.t}</div>
        <button type="button" className="btn sm ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="serif" style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 400 }}>
        {frame.label}
      </div>
      <div className="dim" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
        {frame.summary}
      </div>

      {frame.signed_url && (
        <a
          href={frame.signed_url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "block",
            marginTop: 14,
            border: "1px solid var(--rule)",
            borderRadius: 6,
            overflow: "hidden",
            background: "var(--paper-2)",
          }}
        >
          <img
            src={frame.signed_url}
            alt={frame.label}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </a>
      )}

      <div className="hr" style={{ margin: "16px 0" }} />

      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Expert notes
      </div>
      {frame.notes.length === 0 && (
        <div className="dim" style={{ fontSize: 12, fontStyle: "italic" }}>
          No expert annotated this frame.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {frame.notes.map((n, i) => {
          const e = expertById.get(n.expert_id);
          if (!e) return null;
          return (
            <div
              key={i}
              style={{
                padding: 10,
                borderRadius: 8,
                background: "var(--card)",
                border: "1px solid var(--rule)",
                borderLeft: `3px solid var(--${e.color_token})`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <ExpertMark expert={e} size={18} />
                <span
                  className="mono"
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: `var(--${e.color_token}-deep)`,
                  }}
                >
                  [{e.short_name}]
                </span>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{n.text}</div>
            </div>
          );
        })}
      </div>

      <div className="hr" style={{ margin: "16px 0" }} />

      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Referenced in
      </div>
      {frame.referencedIn.length === 0 ? (
        <div className="dim" style={{ fontSize: 12, fontStyle: "italic" }}>
          Not yet referenced from any artifact.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {frame.referencedIn.map((r, i) => (
            <div key={i} className="mono" style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 4 }}>
              <Icon name="link" size={11} /> {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
